import { Observable, of } from 'rxjs';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, take, filter, timeout, catchError } from 'rxjs/operators';
import { Encoder } from '../utils/encoder';
import { environment } from '../../environments/environment';
import { IframeAuthService } from '../modules/core/services/iframe-auth.service';
import { AuthService } from '../modules/core/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class MemberCanGuard  {

  constructor(
    public router: Router,
    private oidcSecurityService: OidcSecurityService,
    private iframeAuthService: IframeAuthService,
    private authService: AuthService
  ) { }
  canLoad(): Observable<boolean> | boolean {

    // Check if running in iframe mode
    if (this.authService.isIframeMode) {
      return this.checkIframeCanLoad();
    }

    // Standalone mode - use OIDC
    return this.oidcSecurityService.checkAuth()
      .pipe(
        map((response: LoginResponse) => {
          if (response.isAuthenticated) {
            this.clearStorage();
            return true;
          }
          // if user is not authenticated and url doesn't have a hash part save
          // the url as a state to return to after login
          if (window.location.origin.includes(environment.baseURL) || !window.location.hash) {

            this.oidcSecurityService.setState(Encoder.encode(location.href)).subscribe();
            let path = location.href;
            if (URL.parse(path).host.endsWith('.jewelersmutual.com')) {
              path = location.href.split('jewelersmutual.com')[1];
            } else {
              path = location.href.split('localhost:4200')[1];
            }
            sessionStorage.setItem('redirectTo', path);
            localStorage.setItem('savedState', Encoder.encode(location.href));
          }
          this.router.navigate(['/login']);
          return false;
        }),
        take(1)
      );
  }

  private checkIframeCanLoad(): Observable<boolean> {
    const isAuthenticated = this.iframeAuthService.isAuthenticated;

    if (isAuthenticated) {
      this.clearStorage();
      return of(true);
    }

    // In iframe mode, if not authenticated, wait for token from parent window
    // This handles the case where the iframe loads before receiving the first token
    console.log('[MemberCanGuard] Waiting for iframe authentication token...');
    return this.iframeAuthService.tokenReceived$
      .pipe(
        filter(received => received === true),  // Skip initial false value, only proceed when token arrives
        take(1),  // Take the first true value
        timeout(5000),  // Fail after 5 seconds if token never arrives
        map(() => {
          if (this.iframeAuthService.isAuthenticated) {
            this.clearStorage();
            console.log('[MemberCanGuard] Iframe authentication successful');
            return true;
          }
          console.warn('[MemberCanGuard] Token received but authentication failed');
          return false;
        }),
        catchError((err) => {
          console.error('[MemberCanGuard] Timeout waiting for iframe authentication token', err);
          return of(false);
        })
      );
  }

  clearStorage() {
    sessionStorage.setItem('redirectTo', '');
    sessionStorage.setItem('savedState', '');
    localStorage.removeItem('reTryAttempt');
  }
}
